import { HotlineDataType } from "components/LeafletMap/TrackpointControls/HotlineControl";
import { SortDirection } from "components/UI/SortByPicker";
import {
  DEFAULT_MAP_ID,
  MAP_SETTINGS,
  SMALL_VIEWPORT_QUERY,
} from "config/constants";
import { MapColor } from "config/enums/mapColors";
import { MapID } from "config/enums/mapIDs";
import { Overlay } from "config/enums/overlays";
import { Unit } from "config/enums/units";
import type {
  TracksSortByOptions,
  WaypointsSortByOptions,
} from "utils/sorting/itemSorting";
import { StorageSize } from "hooks/useImageUpload";
import { Coords } from "types/app";
import { getQueryParam } from "utils/getQueryParam";
import { getAutomaticLanguage } from "utils/language/getAutomaticLanguage";
import { getAutomaticUnits } from "utils/units/getAutomaticUnits";
import { isValidMapID } from "utils/validators/isValidMapID";

/**
 * The options are split in two: saveable ones are the user's settings, saved to the server and restored on sign in,
 * non-saveable ones are UI state of the current session. Only the saveable ones may ever be sent to the server,
 * see `pickSaveableOptions`.
 */

export type SortOptions<T> = {
  type: T;
  direction: SortDirection;
};

export interface NonSaveableOptions {
  /** Controls whether public tracks should be displayed on map */
  showPublicTracks: boolean;
  /** Controls whether map legend is open or not */
  isLegendOpen: boolean;
  /** Controls whether left menu is open or not */
  isLeftMenuOpen: boolean;
  hotlineType: HotlineDataType;
  allowPremiumFeatures: boolean | undefined;
  storageSize?: StorageSize;
}

export interface SaveableOptions {
  units: Unit;
  /**
   * Selected overlays
   */
  mapOverlays: Overlay[];
  /** BCP-47 language tag */
  language: string;
  /**
   * Controls map color for Refill map (MapID.refill)
   */
  mapColor: MapColor;
  mapType: MapID;
  /**
   * User's saved view zoom (will be restored on login if autoSaveView === true)
   */
  viewZoom: number;
  /**
   * Controls whether view should be automatically saved and also restored after login
   */
  autoSaveView: boolean;
  /**
   * User's saved view center (will be restored on login if autoSaveView === true)
   */
  viewCenter: Coords;
  /** label count percentage (0-100) */
  mapLabels: number;
  sortTracks: SortOptions<TracksSortByOptions>;
  sortWaypoints: SortOptions<WaypointsSortByOptions>;
  colorScheme: string;
  receiveNewsletter: boolean;
}

/**
 * Every saveable option. A `Record`, so TypeScript reports an option that's added to `SaveableOptions`
 * but missing here (or one that's here but not in `SaveableOptions`).
 */
const SAVEABLE_OPTIONS: Record<keyof SaveableOptions, true> = {
  units: true,
  mapOverlays: true,
  language: true,
  mapColor: true,
  mapType: true,
  viewZoom: true,
  autoSaveView: true,
  viewCenter: true,
  mapLabels: true,
  sortTracks: true,
  sortWaypoints: true,
  colorScheme: true,
  receiveNewsletter: true,
};

export const SAVEABLE_OPTION_KEYS = Object.keys(
  SAVEABLE_OPTIONS
) as (keyof SaveableOptions)[];

/**
 * Picks the saveable options that have a value. The user's settings also contain other data
 * (the routing, and non-saveable options that older versions of the app saved to the server by mistake),
 * so they always go through this before they're applied to the options or sent to the server.
 */
export const pickSaveableOptions = (
  settings: Partial<SaveableOptions> | null | undefined
): Partial<SaveableOptions> => {
  const picked: Partial<SaveableOptions> = {};
  if (!settings) return picked;

  const copyOption = <Key extends keyof SaveableOptions>(key: Key) => {
    const value = settings[key];
    if (value !== undefined && value !== null) picked[key] = value;
  };
  SAVEABLE_OPTION_KEYS.forEach(copyOption);

  return picked;
};

/** Options of the current session that an account gets when it doesn't have them saved yet (e.g. a new account) */
const OPTIONS_SAVED_WHEN_MISSING = ["mapType", "units"] as const;

/**
 * The current map and units (detected by the location, or chosen before signing in) for an account that
 * doesn't have them saved (or has a value the app doesn't know), so the next sign in restores them.
 */
export const getMissingSaveableOptions = (
  savedOptions: Partial<SaveableOptions>,
  currentOptions: SaveableOptions
): Partial<SaveableOptions> => {
  const missing: Partial<SaveableOptions> = {};
  const copyOption = <Key extends keyof SaveableOptions>(key: Key) => {
    if (savedOptions[key] === undefined) missing[key] = currentOptions[key];
  };
  OPTIONS_SAVED_WHEN_MISSING.forEach(copyOption);

  return missing;
};

const basemapQueryParam = getQueryParam("basemap") ?? DEFAULT_MAP_ID;
const basemapID = isValidMapID(Number(basemapQueryParam))
  ? Number(basemapQueryParam)
  : DEFAULT_MAP_ID;

export const initialOptionsState: SaveableOptions & NonSaveableOptions = {
  showPublicTracks: false,
  // Detected by the location, like the language by the browser
  units: getAutomaticUnits(),
  mapOverlays: [Overlay.bikeTrails, Overlay.hikeWaymarked],
  language: getAutomaticLanguage() ?? "en",
  mapColor: MapColor.black,
  mapType: basemapID,
  isLegendOpen: false,
  viewZoom: MAP_SETTINGS.zoom || 3,
  autoSaveView: true,
  viewCenter: MAP_SETTINGS.center,
  // Open from the start on larger screens - opening it with an animation after load shifts the map
  // and hurts the Cumulative Layout Shift (Core Web Vitals). On small screens it floats above the map
  // and stays closed. Keep the breakpoint in sync with LeftMenuWrapper.
  isLeftMenuOpen: !window.matchMedia(SMALL_VIEWPORT_QUERY).matches,
  mapLabels: 100,
  hotlineType: "none",
  sortTracks: { type: "name", direction: "asc" },
  sortWaypoints: { type: "name", direction: "asc" },
  colorScheme: "light",
  receiveNewsletter: false,
  allowPremiumFeatures: undefined,
  storageSize: undefined,
};
